// Phase 7 — Track B T-B3. Combobox primitive.
//
// Built on the existing Phase 6 Radix Popover (no new dep). A combobox
// is a text input + filtered list + keyboard nav. Used by the Q-14
// timezone picker in Track C-3 (TimezonePicker.tsx) and any future
// long-list pickers (e.g. category in payments).
//
// Behavioural contract:
//   * `role="combobox"` on the input + `aria-expanded` + `aria-controls`
//   * `role="listbox"` on the popover content
//   * `role="option"` on each rendered item with `aria-selected`
//   * Arrow keys move active item; Enter selects; Escape closes
//   * Type-to-filter narrows the visible list (substring, case-insensitive)
//   * Screen readers receive an `aria-live="polite"` count when the list
//     re-renders (e.g. "5 results")
//
// API:
//   <Combobox
//     value={value}
//     onValueChange={setValue}
//     items={items}
//     placeholder="Search..."
//     emptyMessage="No matches"
//   />

import * as React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './Popover.js';
import { cn } from '../../lib/cn.js';

export interface ComboboxItem {
  /** Stable id used as the selection token. */
  readonly value: string;
  /** Displayed text. Used as both the option label and the input value
   *  when selected. */
  readonly label: string;
}

export interface ComboboxProps {
  /** Currently-selected item value, or null for none. */
  value: string | null;
  onValueChange: (next: string | null) => void;
  items: readonly ComboboxItem[];
  /** Empty-input placeholder. */
  placeholder?: string;
  /** Rendered when filter yields no items. */
  emptyMessage?: string;
  /** Optional id for the input — wired to a parent <label htmlFor=...>. */
  id?: string;
  /** Optional aria-describedby chain (FormField wires this for free). */
  'aria-describedby'?: string;
  /** When true the input + button are disabled. */
  disabled?: boolean;
  className?: string;
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      value,
      onValueChange,
      items,
      placeholder = 'Search…',
      emptyMessage = 'No matches.',
      id,
      'aria-describedby': describedBy,
      disabled,
      className,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [activeIndex, setActiveIndex] = React.useState(0);
    // React.useId() returns colon-prefixed ids (`:r2:`) which are valid HTML
    // ids but invalid as `aria-activedescendant` references because the
    // colon breaks the IDREF lookup (axe `aria-valid-attr-value`). Strip
    // the colons so the resulting id is safe to use as an attribute value
    // and a CSS-id reference.
    const rawListboxId = React.useId();
    const rawAnnounceId = React.useId();
    const listboxId = `cb-${rawListboxId.replace(/:/g, '')}`;
    const announceId = `cb-${rawAnnounceId.replace(/:/g, '')}`;

    // When the parent updates `value`, sync the visible query so the
    // input reflects the current selection while closed.
    const selectedLabel = React.useMemo(() => {
      const hit = items.find((i) => i.value === value);
      return hit?.label ?? '';
    }, [items, value]);

    // The string we render in the input.
    const inputValue = open ? query : selectedLabel;

    const filtered = React.useMemo(() => {
      if (!open) return items;
      const q = query.trim().toLowerCase();
      if (!q) return items;
      return items.filter((i) => i.label.toLowerCase().includes(q));
    }, [items, open, query]);

    React.useEffect(() => {
      if (activeIndex >= filtered.length) setActiveIndex(0);
    }, [filtered.length, activeIndex]);

    function commit(item: ComboboxItem | undefined): void {
      if (!item) return;
      onValueChange(item.value);
      setOpen(false);
      setQuery('');
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
      if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setOpen(true);
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        commit(filtered[activeIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        setQuery('');
      }
    }

    // `aria-activedescendant` must reference an id that EXISTS in the DOM
    // at the moment of evaluation. The listbox is only rendered while the
    // popover is open — so we only emit the attribute while open.
    // Otherwise axe `aria-valid-attr-value` flags the attribute as
    // referencing a non-existent id.
    const activeOptionId =
      open && filtered[activeIndex]
        ? `${listboxId}-${filtered[activeIndex]!.value}`
        : undefined;

    return (
      <div className={cn('relative w-full', className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <input
              ref={ref}
              id={id}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
              aria-describedby={describedBy}
              disabled={disabled}
              value={inputValue}
              placeholder={placeholder}
              onChange={(e) => {
                setQuery(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              className={cn(
                'flex h-10 w-full rounded-md border border-border-default bg-surface-raised px-3 py-2 text-body text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base disabled:cursor-not-allowed disabled:opacity-50',
              )}
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            // Prevent Radix from stealing focus from the input on open —
            // the listbox is keyboard-driven from the input itself.
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="w-[var(--radix-popover-trigger-width)] p-1"
          >
            <ul
              id={listboxId}
              role="listbox"
              className="max-h-64 overflow-y-auto"
            >
              {filtered.length === 0 ? (
                <li
                  role="option"
                  aria-selected={false}
                  className="px-3 py-2 text-body text-text-muted"
                >
                  {emptyMessage}
                </li>
              ) : (
                filtered.map((item, idx) => {
                  const isActive = idx === activeIndex;
                  const isSelected = item.value === value;
                  return (
                    <li
                      key={item.value}
                      id={`${listboxId}-${item.value}`}
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={(e) => {
                        // mousedown so the input doesn't blur first.
                        e.preventDefault();
                        commit(item);
                      }}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={cn(
                        'cursor-default rounded-sm px-3 py-2 text-body text-text-primary',
                        isActive && 'bg-accent-subtle',
                      )}
                    >
                      {item.label}
                    </li>
                  );
                })
              )}
            </ul>
            {/* Live region announces the filtered count to screen readers. */}
            <span id={announceId} aria-live="polite" className="sr-only">
              {filtered.length === 0
                ? emptyMessage
                : `${filtered.length} result${filtered.length === 1 ? '' : 's'}`}
            </span>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
Combobox.displayName = 'Combobox';
